/**
 * Phaser.Game configuration.
 *
 * Horizontal side-scroller (Space Impact lineage) for the Trashstock festival.
 * Game canvas is 960×540 (16:9) and the host page forces landscape orientation
 * via CSS + a `screen.orientation.lock` hint in BootScene. The Phaser scale
 * mode is FIT so the canvas scales uniformly on any phone screen in landscape.
 *
 * Monochrome white-on-black palette — the background colour is pure black
 * and every sprite is white pixels on a transparent background.
 *
 * See: IMPLEMENTATION_PLAN.md §1 (Design Lineage) for the Space Impact
 * reference and §9 (Deployment) for the Railway hosting shape.
 */
import Phaser from "phaser";
import { BootScene } from "./scenes/BootScene";
import { StartScene } from "./scenes/StartScene";
import { GameScene } from "./scenes/GameScene";
import { GameOverScene } from "./scenes/GameOverScene";
import { LeaderboardScene } from "./scenes/LeaderboardScene";

// Horizontal canvas. 960×540 is a 16:9 ratio that fits cleanly inside both
// modern phones (typical landscape viewport ~ 800×360 after browser chrome)
// and desktop browsers. The scale FIT policy will letterbox the difference.
export const GAME_WIDTH = 960;
export const GAME_HEIGHT = 540;

// Module-level handle to the running game instance. Exposed on `window.PHASER`
// by the line below so browser devtools can introspect it (`window.PHASER.game.scene.keys`,
// `window.PHASER.game.scene.getScene("GameScene")`, etc.) — invaluable for
// debugging why the canvas shows nothing on a real phone.
let _game: Phaser.Game | null = null;

export function startGame(parent: HTMLElement | null): void {
  if (!parent) {
    throw new Error("#game element not found in DOM");
  }

// Toggle this flag to show/hide Arcade Physics debug overlays (hitboxes).
const PHYSICS_DEBUG = false;

  const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO, // WebGL if available, else Canvas
    parent,
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
    backgroundColor: "#000000",
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      fullscreenTarget: document.getElementById("app") as HTMLElement | undefined,
    },
    physics: {
      default: "arcade",
      arcade: {
        debug: PHYSICS_DEBUG,
        gravity: { x: 0, y: 0 },
      },
    },
    scene: [BootScene, StartScene, GameScene, GameOverScene, LeaderboardScene],
    // Suppress the default Phaser right-click menu — it interferes with
    // desktop dev testing where right-click is sometimes used to simulate
    // multi-touch on a single mouse.
    disableContextMenu: true,
  };

  _game = new Phaser.Game(config);
  // Expose for browser devtools.
  (window as unknown as { PHASER?: { game: Phaser.Game } }).PHASER = { game: _game };
}